import React from "react";
import { useTranslation } from "react-i18next";
import type { FormikProps } from "formik";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TagInput } from "@/components/ui/tag-input";
import { FormikInput } from "@/components/FormikField";
import {
  FormikErrorMessage,
  useFormikFieldError,
} from "@/components/FormikErrorMessage";
import type { CreateCodeFormValues } from "@/types/generatedCode";

interface CreateCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formik: FormikProps<CreateCodeFormValues>;
  allTags: string[];
}

/**
 * CreateCodeDialog
 *
 * Dialog component for creating a new SILAE code.
 * Handles form validation and submission for code creation.
 */
export const CreateCodeDialog: React.FC<CreateCodeDialogProps> = ({
  open,
  onOpenChange,
  formik,
  allTags,
}) => {
  const { t } = useTranslation();

  // Get error class for description field
  const { errorClass: descriptionErrorClass } = useFormikFieldError(
    formik,
    "description",
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("codeGenerator.createDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("codeGenerator.createDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Code Name Field */}
            <FormikInput
              formik={formik}
              name="name"
              label={`${t("codeGenerator.createDialog.nameLabel")} *`}
              placeholder={t("codeGenerator.createDialog.namePlaceholder")}
              type="text"
            />

            {/* Description Field */}
            <div className="grid gap-2">
              <Label htmlFor="description">
                {t("codeGenerator.createDialog.descriptionLabel")}
                <span className="text-muted-foreground text-xs ml-2">
                  ({t("common.optional")})
                </span>
              </Label>
              <Textarea
                id="description"
                name="description"
                placeholder={t(
                  "codeGenerator.createDialog.descriptionPlaceholder",
                )}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                rows={3}
                disabled={formik.isSubmitting}
                className={descriptionErrorClass}
              />
              <FormikErrorMessage
                formik={
                  formik as unknown as FormikProps<Record<string, unknown>>
                }
                name="description"
              />
            </div>

            {/* Tags Field */}
            <div className="grid gap-2">
              <Label htmlFor="tags">
                {t("codeGenerator.createDialog.tagsLabel")}
                <span className="text-muted-foreground text-xs ml-2">
                  ({t("common.optional")})
                </span>
              </Label>
              <TagInput
                value={formik.values.tags}
                onChange={(tags) => formik.setFieldValue("tags", tags)}
                predefinedTags={allTags}
                placeholder={t("codeGenerator.createDialog.tagsPlaceholder")}
                disabled={formik.isSubmitting}
                maxTags={10}
              />
              <FormikErrorMessage
                formik={
                  formik as unknown as FormikProps<Record<string, unknown>>
                }
                name="tags"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={formik.isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={formik.isSubmitting}>
              {formik.isSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("codeGenerator.createDialog.createButton")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
